import { addBreadcrumb } from '@sentry/react';
import {
  differenceInDays,
  intervalToDuration,
  isAfter,
  parseISO,
} from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { useTickerContext } from '#src/contexts/ticker/index.tsx';
import { sendSafeError } from '#src/lib/error.ts';
import type { TimeOptions } from '#src/lib/time.ts';
import {
  TabNum,
  defaultTimeMessages,
  formatDurationMessage,
  split,
} from '#src/lib/time.ts';

const timeLeftMessage = (
  now: Date,
  time: Date,
  { customMessages, totalDays }: TimeOptions = {}
) => {
  let duration;
  try {
    duration = intervalToDuration({ start: now, end: time });
    if (((duration.months || 0) >= 1 && !duration.days) || totalDays) {
      duration.months = 0;
      duration.days = differenceInDays(time, now);
    }
  } catch {
    addBreadcrumb({
      level: 'error',
      message: `Start date: ${now}\nEnd date: ${time}`,
    });
    sendSafeError(new Error('timeLeftMessage: invalid dates provided'));
    return {
      elements: null,
      message: null,
    };
  }
  const elements = split(duration);
  const message = formatDurationMessage(
    duration,
    elements,
    customMessages || defaultTimeMessages,
    totalDays
  );

  return {
    elements,
    message: (
      <FormattedMessage {...message} values={elements}>
        {txt => <TabNum>{txt}</TabNum>}
      </FormattedMessage>
    ),
  };
};

export const useTimeLeft = (
  timeProp: Nullable<Date | string>,
  options?: TimeOptions
) => {
  const { now } = useTickerContext();

  const time = typeof timeProp === 'string' ? parseISO(timeProp) : timeProp;

  if (!time) {
    return {
      elements: null,
      isEnded: false,
      message: null,
    };
  }

  const isEnded = isAfter(now, time);
  const { elements, message } = timeLeftMessage(now, time, options);

  return {
    elements,
    isEnded,
    message,
  };
};
